// ============================================================================
// Custom Research Preview Slider Component
// ============================================================================
// Article carousel for each category section on research landing page.
// Shows 2 articles side-by-side (desktop) or 1 article (mobile) with horizontal scrolling.
//
// Layout:
// - Left: Category info sidebar (name, description, controls)
// - Right: Horizontally scrolling article cards
//
// Features:
// - Left/right arrow buttons to scroll through articles
// - "See all" button to view full category page
// - Native smooth scrolling (browser's scrollBy)
// - Touch/swipe support on mobile (free with native scroll)
//
// Implementation: Native Browser Scroll
// - Uses browser's scrollBy() method (simple, performant)
// - Automatic boundary detection via scroll events
// - Consistent with case card slider pattern
// - Cross-browser compatible with fallback
//
// Note: Component can be used standalone (mounted individually) or within React Router SPA.
// Navigation handled via onArticleClick prop and window.location (no Router dependency).
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { OthramButton5 } from '@othram/helix';
import { getCategoryDescription } from '../utils/research-helpers';

interface Props {
    category: any;
    onArticleClick: (slug: string) => void;
    isLast?: boolean;
    convertArticleForHelix: (article: any) => any; // Pass down the conversion function
}

// Custom Research Preview Slider Component
export const CustomResearchPreviewSlider: React.FC<Props> = ({ category, onArticleClick, isLast = false, convertArticleForHelix }) => {
    // NATIVE SCROLL APPROACH: Use ref to access DOM element for scrollBy()
    const sliderRef = useRef<HTMLDivElement>(null);
    const [windowWidth, setWindowWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1350);
    const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
    const [canScrollRight, setCanScrollRight] = useState<boolean>(true);
    const [isAnimating, setIsAnimating] = useState<boolean>(false);

    // Safety check
    if (!category || !category.articles || category.articles.length === 0) {
        return null;
    }

    // Responsive articles per view
    const articlesPerView = windowWidth >= 768 ? 2 : 1; // iPad+: 2, Mobile: 1

    // Listen for window resize (used for responsive articlesPerView calculation)
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // NATIVE SCROLL: Update button disabled states based on scroll position
    // This is automatic - browser tells us when we can/can't scroll
    const updateScrollButtons = () => {
        if (!sliderRef.current) return;
        const slider = sliderRef.current;
        setCanScrollLeft(slider.scrollLeft > 0);
        setCanScrollRight(slider.scrollLeft < slider.scrollWidth - slider.clientWidth - 1);
    };

    useEffect(() => {
        const slider = sliderRef.current;
        if (!slider) return;

        updateScrollButtons();
        slider.addEventListener('scroll', updateScrollButtons);
        return () => slider.removeEventListener('scroll', updateScrollButtons);
    }, []);

    // NATIVE SCROLL: Navigate using browser's scrollBy() API
    // Scrolls by one full article width + gap (25px)
    const handlePrevious = () => {
        if (!sliderRef.current || !canScrollLeft || isAnimating) return;
        const slider = sliderRef.current;

        // Prevent rapid successive clicks during animation
        setIsAnimating(true);

        // Calculate scroll distance: article width + 25px gap
        // Robust width detection with fallbacks for initial render edge cases
        let articleWidth = slider.children[0]?.clientWidth || 0;

        // Fallback 1: Try getBoundingClientRect if clientWidth is 0
        if (articleWidth === 0 && slider.children[0]) {
            articleWidth = (slider.children[0] as HTMLElement).getBoundingClientRect().width;
        }

        // Fallback 2: Use sensible default if still 0 (300px typical article card width)
        if (articleWidth === 0) {
            articleWidth = 300;
        }

        const scrollAmount = articleWidth + 25;

        // Cross-browser compatible smooth scroll (matching case card pattern)
        if (document.documentElement && 'scrollBehavior' in document.documentElement.style) {
            slider.scrollBy({
                left: -scrollAmount,
                behavior: 'smooth'  // Browser-native smooth animation
            });
        } else if (typeof slider.scrollBy === 'function') {
            // Fallback for older browsers (no animation)
            slider.scrollBy(-scrollAmount, 0);
        }

        // Re-enable clicking after animation completes (500ms is typical for smooth scroll)
        setTimeout(() => setIsAnimating(false), 500);
    };

    const handleNext = () => {
        if (!sliderRef.current || !canScrollRight || isAnimating) return;
        const slider = sliderRef.current;

        // Prevent rapid successive clicks during animation
        setIsAnimating(true);

        // Calculate scroll distance: article width + 25px gap
        // Robust width detection with fallbacks for initial render edge cases
        let articleWidth = slider.children[0]?.clientWidth || 0;

        // Fallback 1: Try getBoundingClientRect if clientWidth is 0
        if (articleWidth === 0 && slider.children[0]) {
            articleWidth = (slider.children[0] as HTMLElement).getBoundingClientRect().width;
        }

        // Fallback 2: Use sensible default if still 0 (300px typical article card width)
        if (articleWidth === 0) {
            articleWidth = 300;
        }

        const scrollAmount = articleWidth + 25;

        // Cross-browser compatible smooth scroll (matching case card pattern)
        if (document.documentElement && 'scrollBehavior' in document.documentElement.style) {
            slider.scrollBy({
                left: scrollAmount,
                behavior: 'smooth'  // Browser-native smooth animation
            });
        } else if (typeof slider.scrollBy === 'function') {
            // Fallback for older browsers (no animation)
            slider.scrollBy(scrollAmount, 0);
        }

        // Re-enable clicking after animation completes (500ms is typical for smooth scroll)
        setTimeout(() => setIsAnimating(false), 500);
    };

    return (
        <div className="research-category-slider">
            <div className="category-container">
                <div className="category-layout">
                    {/* Left Column - Category Info */}
                    <div className="category-sidebar">
                        <h3 className="category-title">
                            {category.name}
                        </h3>
                        <p className="category-description">
                            {getCategoryDescription(category.slug)}
                        </p>
                        <div className="category-navigation-horizontal">
                            <div className="category-nav-arrows">
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={handlePrevious}
                                    disabled={!canScrollLeft}
                                >
                                    <span style={{ position: 'relative', top: '-1px' }}>←</span>
                                </button>
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={handleNext}
                                    disabled={!canScrollRight}
                                >
                                    <span style={{ position: 'relative', top: '-1px' }}>→</span>
                                </button>
                            </div>
                            <OthramButton5
                                variant="outline-secondary"
                                onClick={() => window.location.href = `/research/${category.slug}`}
                            >
                                See all
                            </OthramButton5>
                        </div>
                    </div>

                    {/* Right Column - Articles Carousel */}
                    {/* NATIVE SCROLL: Single container with overflow and ref for scrollBy() */}
                    <div
                        ref={sliderRef}
                        className="category-articles"
                        style={{
                            display: 'flex',
                            overflow: 'hidden',      // Hide overflow but allow programmatic scroll
                            overflowX: 'auto',       // Enable horizontal scrolling
                            scrollbarWidth: 'none',  // Hide scrollbar (Firefox)
                            msOverflowStyle: 'none'  // Hide scrollbar (IE/Edge)
                        }}
                    >
                        {/* Additional scrollbar hiding for WebKit browsers in SCSS */}
                        {category.articles.map((article: any, index: number) => {
                            const converted = convertArticleForHelix(article);
                            const isLastArticle = index === category.articles.length - 1;
                            return (
                                <article
                                    key={article.id}
                                    className="research-category-article"
                                    onClick={() => onArticleClick(article.slug)}
                                    style={{
                                        width: `calc((100% - ${(articlesPerView - 1) * 25}px) / ${articlesPerView})`,
                                        marginRight: isLastArticle ? '0' : '25px',
                                        flexShrink: 0
                                    }}
                                >
                                    <div className="category-article-image">
                                        <img
                                            src={converted.imageUrl}
                                            alt={converted.imageAlt}
                                        />
                                        <span className="research-tag-overlay research-tag-overlay-image">
                                            {converted.tag}
                                        </span>
                                    </div>
                                    <div className="category-article-content">
                                        <span className="research-tag-overlay research-tag-overlay-content">
                                            {converted.tag}
                                        </span>
                                        <h4 className="category-article-title">
                                            {converted.title}
                                        </h4>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                    {/* END: Native Scroll Carousel Container */}
                </div>
            </div>
            {/* Conditional divider - show for all categories except the last one */}
            {!isLast && (
                <div className="research-divider-split category-divider"></div>
            )}
        </div>
    );
};
